import { createFileRoute } from "@tanstack/react-router";
import { DriverLandingPage, type DriverLandingContent } from "@/components/DriverLandingPage";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";
import { getPublicConfig } from "@/lib/site-content.functions";

const CONTENT: DriverLandingContent = {
  eyebrow: "Uber & TfL Private-Hire MOT",
  h1: "Uber MOT & TfL Private-Hire Inspection Support, East London",
  intro:
    "Uber and TfL private-hire drivers need MOT-compliant, well-presented vehicles to keep working. Woodseer Autos is a DVSA-authorised MOT station in Poplar, E14 — we test, advise and (where it's safe) fix MOT issues the same day so you can get back on shift.",
  trustChips: [
    "DVSA-authorised MOT station V106376",
    "Classes 4, 5 & 7 covered",
    "Pre-MOT checks available",
    "Same-day retest where possible",
    "Mon–Sat 9am–6pm",
  ],
  audienceTitle: "Who this page is for",
  audience: [
    "Uber drivers renewing TfL PHV licence",
    "Bolt and FREE NOW drivers",
    "Private-hire and minicab drivers",
    "Executive PHV operators",
    "Multi-car PHV fleet owners",
    "Drivers preparing for TfL inspection",
  ],
  helpTitle: "What we cover",
  helpItems: [
    "Standard MOT (class 4) — cars and small vans",
    "Class 5 MOT — minibuses up to 13 seats",
    "Class 7 MOT — goods vehicles up to 3,500kg",
    "Pre-MOT check (no fail, no surprises)",
    "Same-day fix-and-retest where possible",
    "Brake, tyre and emissions advisories",
    "Wheel alignment and suspension checks",
    "Diagnostics for warning lights ahead of test",
  ],
  issuesTitle: "Common MOT fails on private-hire cars",
  issues: [
    "Brake pads or discs below 1.5mm",
    "Tyre tread below 1.6mm on inner edge",
    "Suspension knocks (drop links, bushes)",
    "Headlight aim out of spec",
    "Number plate light blown",
    "Emissions over limit on diesels (DPF)",
    "ABS / airbag / engine management lights on",
    "Worn wipers smearing the screen",
  ],
  valueTitle: "What to do before your TfL inspection",
  valueBody:
    "TfL private-hire inspections are stricter than a standard MOT — cosmetic condition, seatbelts, signage and interior cleanliness all matter. We recommend a pre-MOT check at Woodseer first so you can fix anything that would fail. We'll tell you honestly what TfL is likely to pull you up on and quote any work clearly before it starts.",
  beforeTitle: "Before you arrive",
  beforeItems: [
    "Bring your V5C (logbook)",
    "Bring your TfL PHV licence and any paperwork",
    "Note your MOT or PHV licence renewal date",
    "Clear any rubbish or loose items from the boot",
    "Flag any warning lights or noises",
    "Bring the locking wheel nut key if fitted",
  ],
  faqs: [
    { q: "Can you MOT my Uber car?", a: "Yes — most Uber vehicles are class 4. Some larger PHV vehicles (minibus, large van) are class 5 or 7. We test all three at VTS V106376." },
    { q: "Do you do TfL private-hire inspections?", a: "TfL PHV inspections are carried out by TfL-approved testing centres only. We help you prepare — pre-inspection check, MOT and any repairs needed to pass. Call us with your reg and renewal date and we'll walk you through it." },
    { q: "Will an MOT fail affect my Uber account?", a: "Uber requires a valid MOT to drive. If your car fails, we'll explain exactly what's needed, quote clearly, and retest as soon as work is finished — usually the same day where parts are in stock." },
    { q: "Can you do a pre-MOT check?", a: "Yes. A pre-MOT check is cheaper than failing and being charged for a retest — we run through the common fail items and tell you what needs sorting before the official test." },
    { q: "Do you fit OE-equivalent parts for working cars?", a: "Yes — good, better and best options on most parts. We balance cost against longevity so you're not back in three months." },
    { q: "How quickly can I book in?", a: "Most weeks we have an MOT slot within 24–48 hours. Call us with your reg and we'll find the earliest gap." },
  ],
  bookingContext: "uber-mot",
  waPrefill: "I'm an Uber / private-hire driver and need an MOT. Reg and PHV expiry: ",
  relatedLinks: [
    { to: "/pco-mot-east-london", label: "PCO / private-hire garage" },
    { to: "/garage-for-uber-bolt-drivers-east-london", label: "Uber & Bolt driver garage" },
    { to: "/mot", label: "MOT testing" },
    { to: "/mot-checklist-before-test", label: "Pre-MOT checklist" },
    { to: "/cost/mot-east-london", label: "MOT cost guide" },
    { to: "/diagnostics", label: "Diagnostics" },
    { to: "/value-for-money-garage-east-london", label: "Value-for-money garage" },
    { to: "/google-maps-garage-e14", label: "Find us — sat-nav & yard entrance" },
  ],
};

export const Route = createFileRoute("/uber-mot-east-london")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/uber-mot-east-london",
      title: "Uber MOT East London | PHV MOT & TfL Inspection Prep | Woodseer Autos",
      description:
        "Uber, Bolt and TfL private-hire MOT testing in East London. Pre-MOT checks, same-day retest where possible. DVSA station V106376 in Poplar, E14.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Uber MOT East London", path: "/uber-mot-east-london" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        serviceSchema({
          name: "Uber MOT & TfL Private-Hire Inspection Support",
          serviceType: "MOT Testing",
          description:
            "MOT testing and TfL private-hire inspection preparation for Uber, Bolt and minicab drivers in East London. DVSA-authorised station V106376.",
          areaServed: "East London, E14",
          url: "https://woodseerautos.co.uk/uber-mot-east-london",
        }),
        faqSchema(CONTENT.faqs) as Record<string, unknown>,
      ],
    }),
  component: () => <DriverLandingPage content={CONTENT} path="/uber-mot-east-london" />,
});
